/**
 * Homepage reviews carousel.
 * Loads published Judge.me reviews once and renders them in a native scroll-snap track.
 */
(function () {
  const ROOT_SELECTOR = '.judgeme-reviews-showcase';
  const STORE_REVIEW_NAME = 'endless & co';
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

  /** @type {Map<string, Promise<string>>} */
  const productImageRequests = new Map();

  /**
   * @param {string} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  function isAllCaps(text) {
    const letters = text.replace(/[^a-zA-Z]/g, '');
    return letters.length > 2 && letters === letters.toUpperCase();
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function toTitleCase(text) {
    return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function stripOuterQuotes(text) {
    return text.replace(/^[\s"'“”‘’«»]+|[\s"'“”‘’«»]+$/g, '').trim();
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function formatQuote(text) {
    const stripped = stripOuterQuotes(text);
    const value = isAllCaps(stripped) ? toTitleCase(stripped) : stripped;
    return `\u201C${value}\u201D`;
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function formatProductName(text) {
    const value = text.replace(/\s+/g, ' ').trim();
    return isAllCaps(value) ? toTitleCase(value) : value;
  }

  /**
   * @param {{ product_title?: string }} review
   * @returns {boolean}
   */
  function isStoreReview(review) {
    const name = (review.product_title || '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[.]+$/g, '')
      .toLowerCase();
    return name === STORE_REVIEW_NAME;
  }

  /**
   * @param {{ product_url?: string }} review
   * @returns {string}
   */
  function productHandle(review) {
    const match = String(review.product_url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  /**
   * @param {{ picture?: { urls?: Record<string, string> }, pictures_urls?: Array<string | { huge?: string, original?: string }> }} review
   * @returns {string}
   */
  function reviewPhoto(review) {
    const urls = review.picture && review.picture.urls;
    if (urls) return urls.huge || urls.original || urls.compact || urls.small || '';

    const first = Array.isArray(review.pictures_urls) ? review.pictures_urls[0] : '';
    if (!first) return '';
    if (typeof first === 'string') return first;
    return first.huge || first.original || '';
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  function productFallbackImage(handle) {
    if (!handle) return Promise.resolve('');
    if (productImageRequests.has(handle)) return productImageRequests.get(handle);

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        const images = product && product.images;
        if (!images || !images.length) return '';
        return images[1] || images[0] || '';
      })
      .catch(() => '');

    productImageRequests.set(handle, request);
    return request;
  }

  /**
   * @param {HTMLElement} root
   * @returns {string}
   */
  function reviewsUrl(root) {
    const params = new URLSearchParams({
      reviews_selection: 'all',
      carousel_type: 'cards',
      star_rating: '4_to_5_star',
      max_reviews: '12',
      url: root.dataset.shopUrl || window.location.origin,
      shop_domain: root.dataset.shopDomain || '',
      platform: 'shopify',
      primary_language: (document.documentElement.lang || 'en').slice(0, 2),
      display_order: 'most_recent',
    });

    return `https://cdn.judge.me/reviews/reviews_for_carousel?${params}`;
  }

  /**
   * @param {HTMLElement} track
   * @returns {HTMLElement[]}
   */
  function visibleCards(track) {
    return [...track.querySelectorAll('.reviews-card')].filter(
      (card) => card instanceof HTMLElement && getComputedStyle(card).display !== 'none'
    );
  }

  /**
   * Mouse drag follows the pointer, then snaps to a card.
   * Touch swipes use the track's native scroll, same as the homepage slideshow.
   * @param {HTMLElement} track
   */
  function enablePointerSwipe(track) {
    track.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('a, button')) return;

      event.preventDefault();

      const startX = event.clientX;
      let previous = startX;
      let velocity = 0;
      let moved = false;
      let distance = 0;
      const startTime = performance.now();
      const cardsAtStart = visibleCards(track);
      let startIndex = 0;
      let bestStart = Infinity;
      cardsAtStart.forEach((card, cardIndex) => {
        const delta = Math.abs(card.offsetLeft - track.scrollLeft);
        if (delta < bestStart) {
          bestStart = delta;
          startIndex = cardIndex;
        }
      });
      const controller = new AbortController();
      const { signal } = controller;

      const onMove = (moveEvent) => {
        const current = moveEvent.clientX;
        const initialDelta = startX - current;
        if (!initialDelta) return;

        if (!moved) {
          if (Math.abs(initialDelta) < 4) return;
          moved = true;
          track.classList.add('is-dragging');
          document.addEventListener('click', (clickEvent) => clickEvent.preventDefault(), {
            capture: true,
            once: true,
            signal,
          });
        }

        const delta = previous - current;
        const timeDelta = Math.max(performance.now() - startTime, 1);
        velocity = Math.round((delta / timeDelta) * 1000);
        previous = current;
        distance += Math.abs(delta);
        track.scrollLeft += delta;
      };

      const onUp = () => {
        controller.abort();
        if (!moved) {
          track.classList.remove('is-dragging');
          return;
        }

        const cards = visibleCards(track);
        let index = 0;
        let best = Infinity;
        cards.forEach((card, cardIndex) => {
          const delta = Math.abs(card.offsetLeft - track.scrollLeft);
          if (delta < best) {
            best = delta;
            index = cardIndex;
          }
        });

        const direction = Math.sign(velocity);
        const flicked = startIndex === index && Math.abs(velocity) >= 10 && distance >= 10;
        if (flicked) index += direction;
        index = Math.min(cards.length - 1, Math.max(0, index));

        const card = cards[index];
        if (!card || Math.abs(track.scrollLeft - card.offsetLeft) < 2) {
          track.classList.remove('is-dragging');
          return;
        }

        track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
        track.addEventListener(
          'scrollend',
          () => track.classList.remove('is-dragging'),
          { once: true }
        );
      };

      document.addEventListener('mousemove', onMove, { signal });
      document.addEventListener('mouseup', onUp, { signal });
    });
  }

  /**
   * @param {HTMLElement} track
   * @param {number} direction
   */
  function scrollByCard(track, direction) {
    const card = visibleCards(track)[0];
    if (!card) return;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 16;
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  /**
   * @param {HTMLElement} dots
   * @param {HTMLElement} card
   */
  function setActiveDot(dots, card) {
    const index = card.dataset.reviewIndex;
    dots.querySelectorAll('.reviews-dots__dot').forEach((button) => {
      const isActive = button instanceof HTMLButtonElement && button.dataset.reviewIndex === index;
      button.classList.toggle('is-active', isActive);
      if (isActive) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} dots
   */
  function renderDots(track, dots) {
    dots.replaceChildren();

    visibleCards(track).forEach((card, index) => {
      card.dataset.reviewIndex = String(index);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'reviews-dots__dot';
      button.setAttribute('role', 'tab');
      button.dataset.reviewIndex = String(index);
      button.setAttribute('aria-label', `Go to review ${index + 1}`);
      button.addEventListener('click', () => {
        track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      });
      dots.appendChild(button);
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {Array<Record<string, any>>} reviews
   */
  function renderCards(root, reviews) {
    const track = root.querySelector('[data-reviews-track]');
    if (!(track instanceof HTMLElement)) return;

    const cards = reviews
      .filter((review) => review && review.body && !isStoreReview(review))
      .map((review) => {
        const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
        const stars = [1, 2, 3, 4, 5]
          .map((star) => `<span class="${star <= rating ? 'is-on' : ''}" aria-hidden="true">★</span>`)
          .join('');
        const name = formatProductName(review.reviewer_name || 'Customer');
        const productName = formatProductName(review.product_title || '');
        const productPath = String(review.product_url || '');
        const productLink = productPath.startsWith('/products/')
          ? `<a class="reviews-card__product" href="${escapeHtml(productPath)}">${escapeHtml(productName)}</a>`
          : `<p class="reviews-card__product">${escapeHtml(productName)}</p>`;
        const verified = review.verified_buyer
          ? '<span class="reviews-card__verified" aria-label="Verified buyer">✓</span>'
          : '';
        const photo = reviewPhoto(review);
        const variantImage = review.product_variant_image_url || '';

        return `<article class="reviews-card" data-product-handle="${escapeHtml(productHandle(review))}" ${
          photo ? '' : `data-needs-image="true" data-variant-image="${escapeHtml(variantImage)}"`
        }>
          <p class="reviews-card__stars" aria-label="${rating} out of 5 stars">${stars}</p>
          <p class="reviews-card__quote">${escapeHtml(formatQuote(review.body))}</p>
          <p class="reviews-card__name"><span>${escapeHtml(name)}</span>${verified}</p>
          ${productLink}
          ${
            photo
              ? `<img class="reviews-card__image" alt="" loading="lazy" src="${escapeHtml(photo)}">`
              : ''
          }
        </article>`;
      });

    track.innerHTML = cards.join('');

    track.querySelectorAll('[data-needs-image]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const handle = card.dataset.productHandle || '';
      productFallbackImage(handle).then((url) => {
        const src = url || card.dataset.variantImage || '';
        if (!src || card.querySelector('.reviews-card__image')) return;
        const image = document.createElement('img');
        image.className = 'reviews-card__image';
        image.alt = '';
        image.loading = 'lazy';
        image.src = src;
        card.appendChild(image);
      });
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initCarousel(root) {
    const track = root.querySelector('[data-reviews-track]');
    const dots = root.querySelector('[data-reviews-dots]');
    if (!(track instanceof HTMLElement) || !(dots instanceof HTMLElement)) return;

    /** @type {IntersectionObserver | null} */
    let observer = null;

    const observeCards = () => {
      if (observer) observer.disconnect();
      renderDots(track, dots);

      const cards = visibleCards(track);
      if (track.scrollLeft <= 1 && cards[0]) setActiveDot(dots, cards[0]);

      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting);
          if (!visible.length) return;
          visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible[0].target instanceof HTMLElement) setActiveDot(dots, visible[0].target);
        },
        {
          root: track,
          rootMargin: '0px -70% 0px 0px',
          threshold: [0.15, 0.4, 0.6, 0.9],
        }
      );

      cards.forEach((card) => observer.observe(card));
    };

    enablePointerSwipe(track);

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollByCard(track, 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollByCard(track, -1);
      }
    });

    observeCards();
    MOBILE_MQ.addEventListener('change', observeCards);
    window.addEventListener('resize', observeCards, { passive: true });
  }

  async function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) return;
    if (!root.dataset.shopDomain) return;

    try {
      const response = await fetch(reviewsUrl(root));
      if (!response.ok) return;
      const data = await response.json();
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];
      renderCards(root, reviews);
      initCarousel(root);
    } catch {
      // Leave the track empty if the reviews feed is unavailable.
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
